import { DailyPortfolioValue, PortfolioHistoryRange, ChartDataPoint } from '../types/portfolioHistory'
import { apiFetch } from '../lib/api'

export class PortfolioHistoryService {
  // Tracks portfolioId_date pairs already confirmed/saved this session — prevents redundant GET checks
  private savedSnapshots = new Set<string>()

  private getBangkokDateString(date = new Date()): string {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Bangkok',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(date)

    const partMap = Object.fromEntries(parts.map(part => [part.type, part.value]))
    return `${partMap.year}-${partMap.month}-${partMap.day}`
  }

  /**
   * Save daily portfolio value snapshot
   */
  async saveDailySnapshot(portfolioId: number | null, data: {
    totalValue: number
    totalCost: number
    gainLoss: number
    gainLossPercent: number
    currency: 'USD' | 'THB'
  }): Promise<void> {
    try {
      const response = await apiFetch('/api/portfolio-history', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          portfolio_id: portfolioId,
          date: this.getBangkokDateString(),
          total_value: data.totalValue,
          total_cost: data.totalCost,
          gain_loss: data.gainLoss,
          gain_loss_percent: data.gainLossPercent,
          currency: data.currency
        })
      })

      if (!response.ok) {
        throw new Error('Failed to save portfolio snapshot')
      }
    } catch (error) {
      console.error('Error saving portfolio snapshot:', error)
      throw error
    }
  }

  /**
   * Get portfolio history for a specific portfolio and date range
   */
  async getPortfolioHistory(
    portfolioId: number | null,
    range: PortfolioHistoryRange,
    currency?: 'USD' | 'THB',
    signal?: AbortSignal
  ): Promise<DailyPortfolioValue[]> {
    try {
      const params = new URLSearchParams({
        portfolio_id: portfolioId?.toString() || 'null',
        period: range.period,
        start_date: range.startDate,
        end_date: range.endDate
      })

      if (currency) {
        params.set('currency', currency)
      }

      const response = await apiFetch(`/api/portfolio-history?${params}`, { signal })

      if (!response.ok) {
        throw new Error('Failed to fetch portfolio history')
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching portfolio history:', error)
      throw error
    }
  }

  /**
   * Convert daily values to chart data points
   */
  formatChartData(dailyValues: DailyPortfolioValue[]): ChartDataPoint[] {
    return dailyValues.map(item => ({
      date: item.date,
      value: item.total_value,
      cost: item.total_cost,
      gainLoss: item.gain_loss,
      gainLossPercent: item.gain_loss_percent,
      formattedDate: this.formatDate(item.date)
    }))
  }

  /**
   * Generate missing data points for smoother chart
   */
  generateMissingDataPoints(
    chartData: ChartDataPoint[], 
    startDate: string, 
    endDate: string
  ): ChartDataPoint[] {
    if (chartData.length === 0) return []

    const result: ChartDataPoint[] = []
    const existingData = new Map(chartData.map(item => [item.date, item]))
    const nextDate = (dateString: string) => {
      const date = new Date(`${dateString}T00:00:00.000Z`)
      date.setUTCDate(date.getUTCDate() + 1)
      return date.toISOString().split('T')[0]
    }

    // Forward fill missing dates
    let lastKnownData = chartData[0]
    
    for (let dateString = startDate; dateString <= endDate; dateString = nextDate(dateString)) {
      if (existingData.has(dateString)) {
        const data = existingData.get(dateString)!
        lastKnownData = data
        result.push(data)
      } else {
        // Use last known values for missing dates
        result.push({
          date: dateString,
          value: lastKnownData.value,
          cost: lastKnownData.cost,
          gainLoss: lastKnownData.gainLoss,
          gainLossPercent: lastKnownData.gainLossPercent,
          formattedDate: this.formatDate(dateString)
        })
      }
    }

    return result
  }

  /**
   * Get date range for different periods
   */
  getDateRange(period: 'week' | 'month' | '3months' | '6months' | 'year' | 'all'): PortfolioHistoryRange {
    const today = new Date(`${this.getBangkokDateString()}T00:00:00+07:00`)
    const startDate = new Date(today)
    
    switch (period) {
      case 'week':
        startDate.setDate(today.getDate() - 7)
        break
      case 'month':
        startDate.setMonth(today.getMonth() - 1)
        break
      case '3months':
        startDate.setMonth(today.getMonth() - 3)
        break
      case '6months':
        startDate.setMonth(today.getMonth() - 6)
        break
      case 'year':
        startDate.setFullYear(today.getFullYear() - 1)
        break
      case 'all':
        startDate.setFullYear(2020) // Arbitrary start date
        break
    }

    return {
      startDate: this.getBangkokDateString(startDate),
      endDate: this.getBangkokDateString(today),
      period
    }
  }

  /**
   * Format date for display
   */
  private formatDate(dateString: string): string {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('th-TH', {
      day: 'numeric',
      month: 'short',
      year: '2-digit'
    }).format(date)
  }

  /**
   * Auto-save daily snapshot (should be called when portfolio values change)
   */
  async autoSaveSnapshot(portfolioId: number | null, portfolioSummary: {
    totalValue: number
    totalCost: number
    totalGainLoss: number
    gainLossPercent: number
  }, currency: 'USD' | 'THB'): Promise<void> {
    try {
      const today = this.getBangkokDateString()
      const cacheKey = `${portfolioId}_${today}`

      if (this.savedSnapshots.has(cacheKey)) return

      // Guard: skip if portfolio hasn't loaded yet (prevents saving zeros or partial data)
      if (portfolioSummary.totalValue <= 0) return

      // Always overwrite with latest prices — server upserts on (date, currency, user_id)
      await this.saveDailySnapshot(portfolioId, {
        totalValue: portfolioSummary.totalValue,
        totalCost: portfolioSummary.totalCost,
        gainLoss: portfolioSummary.totalGainLoss,
        gainLossPercent: portfolioSummary.gainLossPercent,
        currency
      })

      this.savedSnapshots.add(cacheKey)
    } catch (error) {
      console.warn('Failed to auto-save portfolio snapshot:', error)
    }
  }
}

// Export singleton instance
export const portfolioHistoryService = new PortfolioHistoryService()
